/**
 * Saves the profile page to Bullhorn [KAP-7; decision 0033].
 *
 * **Called the moment the page is finished, before a job is picked.** 0033's reason is not tidiness: a
 * candidate who fills this in and walks away must still exist — *"what if they want to talk to a recruiter
 * but don't end up sticking around? And then we've lost all of that information"*. The repeat-entry a
 * returning person hits is the second symptom, not the first.
 *
 * ⚠️ **The full SSN and the date of birth still never travel**, and cannot: the kiosk collects neither a
 * complete social security number nor a year of birth, and the backend refuses a payload carrying either
 * rather than dropping it quietly [decision 0005].
 *
 * What DOES travel, on a create only, is the pair of PARTIAL factors below — four digits and a
 * month-and-day. Decision 0132 drew that line after a candidate created at the kiosk came back the next day
 * and was asked to fill in the same profile again: the lookup is a four-factor AND, its last two factors
 * live in fields nothing was populating, so every record the kiosk made was invisible to the kiosk.
 */

import { kioskFetch } from './kioskFetch';
import { refusedFieldsIn } from './refusedFields';

export interface ProfilePayload {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phone: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
  readonly payRate: string;
  readonly startDate: string;
  /** One of Bullhorn's own Shift Type values, or absent — free text would poison a picklist field. */
  readonly shiftPreference?: string;
  /** The street line. Bullhorn merges a partial address, so this never disturbs city/state/ZIP. */
  readonly homeAddress?: string;
  readonly emergencyContact?: string;
  /** Ten digits, same rule as `phone`. */
  readonly emergencyPhone?: string;
  /** The contact-consent tick. Sent only when TRUE — the screen cannot produce a `false`. */
  readonly consent?: boolean;
  /** Present when the lookup matched somebody: it makes this an update, never a second record. */
  readonly candidateId?: number;
  /**
   * The two lookup factors, from check-in — **on a create only** [decision 0132].
   *
   * A record reached through the update path was reached BY matching on these, so it already holds them;
   * sending them again would let a mistyped check-in overwrite a good value on a real person's record. The
   * API enforces the same asymmetry, so this is belt and braces rather than the only guard.
   *
   * `ssnLast4` is a string because `0123` is a valid last-4 and a number drops the zero. Month and day carry
   * **no year** — the kiosk never asks for one.
   */
  readonly ssnLast4?: string;
  readonly birthMonth?: number;
  readonly birthDay?: number;
}

/**
 * A returning candidate's UPDATE: **only the fields they changed**, and the id that makes it an update
 * [0464 R3–R7].
 *
 * An absent field is one the record keeps — never `''`, which reads as "untouched" only by convention, and
 * never the record's own value written back over itself. The api takes an update with any field absent
 * (iss-kiosk-api PR 386). `consent` travels only when the candidate was asked it on this visit and agreed —
 * the record held none [0507] — and never when it was already on file. No lookup factor travels, for the
 * reason on {@link ProfilePayload.ssnLast4}.
 */
export type ProfileUpdatePayload = Partial<
  Pick<
    ProfilePayload,
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phone'
    | 'payRate'
    | 'startDate'
    | 'shiftPreference'
    | 'homeAddress'
    | 'emergencyContact'
    | 'emergencyPhone'
  >
> & { readonly candidateId: number; readonly consent?: true };

export type SaveProfileOutcome =
  | { readonly kind: 'saved'; readonly candidateId: number }
  /**
   * The api REFUSED the payload — a 400 from its validation — and named the fields it would not take.
   *
   * **Split out of `unreachable` deliberately, because the two differ in the only way that matters to the
   * person standing there: whether they can do anything about it.** A returning candidate whose record
   * holds `+541125484747` was watching `POST /candidates` come back 400 — *"phone must match
   * /^[0-9]{10}$/"* — while the screen moved on to the arrival menu, so they left believing a correction
   * that never happened had been saved (observed on dev, 2026-09-07).
   *
   * `fields` carries the api's OWN field names, not this app's: mapping them onto a screen's controls is
   * the screen's job, because only it knows what it draws.
   */
  | { readonly kind: 'rejected'; readonly fields: readonly string[] }
  /**
   * The record does not exist.
   *
   * One value for every failure the candidate cannot act on: writes disabled, Bullhorn refused, a dead
   * socket. They are different lines in a log and the same fact to the person standing there — and there is
   * nothing for them to do about it, so the flow continues and the gap is the operator's to see [BR-K7].
   */
  | { readonly kind: 'unreachable' };

const REQUEST_TIMEOUT_MS = 10_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

export async function saveProfile(
  payload: ProfilePayload | ProfileUpdatePayload,
): Promise<SaveProfileOutcome> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}/candidates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unreachable' };
  }

  // A 400 is the api saying no to THIS payload, and it is the one failure the candidate can fix.
  if (response.status === 400) return { kind: 'rejected', fields: await refusedFields(response) };
  if (!response.ok) return { kind: 'unreachable' };

  const body: unknown = await response.json().catch(() => null);
  if (typeof body !== 'object' || body === null) return { kind: 'unreachable' };
  const { candidateId } = body as { candidateId?: unknown };
  // Without an id there is no record to hang the submission on, which is the same fact as no candidate.
  if (typeof candidateId !== 'number') return { kind: 'unreachable' };

  return { kind: 'saved', candidateId };
}

/** The fields a 400 names — the parse is shared with the identity lookup [`refusedFields.ts`]. */
async function refusedFields(response: Response): Promise<readonly string[]> {
  return refusedFieldsIn(await response.json().catch(() => null));
}
